package top.wcpe.allincore.dsl.compiler.semantic.`native`.methods

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.semantic.`native`.Arity
import top.wcpe.allincore.dsl.compiler.semantic.`native`.MethodDescriptor
import top.wcpe.allincore.dsl.compiler.semantic.`native`.ResolveCtx

/**
 * 内置 object `local` 的方法占位描述符集合。
 *
 * 包含 openScreen / back / close / themes / setTheme / playSound 等方法。
 */

/** local.openScreen(screenId) 或 local.openScreen(screenId, args)。 */
object LocalOpenScreenMethod : MethodDescriptor {
    override val name: String = "openScreen"
    override val arity: Arity = Arity.Range(1, 2)

    /** 占位实现：直接返回 null，不做任何诊断。 */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? = null
}

/** local.back()。 */
object LocalBackMethod : MethodDescriptor {
    override val name: String = "back"
    override val arity: Arity = Arity.Fixed(0)

    /** 占位实现：直接返回 null，不做任何诊断。 */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? = null
}

/** local.close()。 */
object LocalCloseMethod : MethodDescriptor {
    override val name: String = "close"
    override val arity: Arity = Arity.Fixed(0)

    /** 占位实现：直接返回 null，不做任何诊断。 */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? = null
}

/** local.themes()：读取当前 IR 的只读主题目录。 */
object LocalThemesMethod : MethodDescriptor {
    override val name: String = "themes"
    override val arity: Arity = Arity.Fixed(0)

    /** 占位实现：直接返回 null，不做任何诊断。 */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? = null
}

/** local.setTheme(themeName)。 */
object LocalSetThemeMethod : MethodDescriptor {
    override val name: String = "setTheme"
    override val arity: Arity = Arity.Fixed(1)

    /** 占位实现：直接返回 null，不做任何诊断。 */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? = null
}

/** local.command(key)：按 key 执行服务端 ui-commands.yml 中配置的命令。 */
object LocalCommandMethod : MethodDescriptor {
    override val name: String = "command"
    override val arity: Arity = Arity.Fixed(1)

    /** 占位实现：直接返回 null，不做任何诊断。 */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? = null
}

/** local.playSound(soundId[, volume[, pitch]])：播放本地二维音效。 */
object LocalPlaySoundMethod : MethodDescriptor {
    override val name: String = "playSound"
    override val arity: Arity = Arity.Range(1, 3)

    /** 占位实现：直接返回 null，不做任何诊断。 */
    override fun resolve(call: ExprAst.MemberCall, diag: DiagnosticCollector, ctx: ResolveCtx): ExprAst? = null
}

/** 聚合 local 宿主 object 的所有方法描述符。 */
val LocalApiMethodMap: Map<String, MethodDescriptor> = buildLocalMethodMap(
    LocalOpenScreenMethod,
    LocalBackMethod,
    LocalCloseMethod,
    LocalThemesMethod,
    LocalSetThemeMethod,
    LocalCommandMethod,
    LocalPlaySoundMethod,
)

/** 构建方法映射，检测重复注册。 */
private fun buildLocalMethodMap(vararg methods: MethodDescriptor): Map<String, MethodDescriptor> {
    val result = linkedMapOf<String, MethodDescriptor>()
    for (m in methods) {
        if (result.containsKey(m.name)) {
            error("LocalApiMethodMap 中方法名 '${m.name}' 重复注册")
        }
        result[m.name] = m
    }
    return result
}
